import { safeDeleteSheet } from "@/lib/office-helpers";
import {
  type FuncionProjectSpec,
  type GridSpec,
  type LabelledCell,
  type TableSpec,
  allHeaders,
  columnLetter,
  gridBodyRange,
  parseAddress,
  tableColumnRange,
} from "../data/funciones/spec";

const ANSWER_FILL = "#FFF2CC";
const ANSWER_BORDER = "#E0B84F";
const LABEL_COLOR = "#1E3F6D";

function markAnswer(range: Excel.Range) {
  range.format.fill.color = ANSWER_FILL;
  for (const edge of [
    Excel.BorderIndex.edgeTop,
    Excel.BorderIndex.edgeBottom,
    Excel.BorderIndex.edgeLeft,
    Excel.BorderIndex.edgeRight,
  ]) {
    range.format.borders.getItem(edge).style = Excel.BorderLineStyle.continuous;
    range.format.borders.getItem(edge).color = ANSWER_BORDER;
  }
}

function writeLabelled(ws: Excel.Worksheet, cell: LabelledCell, isAnswer: boolean) {
  const { col, row } = parseAddress(cell.address);
  const label =
    cell.labelPosition === "above"
      ? ws.getRange(`${columnLetter(col)}${row - 1}`)
      : ws.getRange(`${columnLetter(col - 1)}${row}`);
  label.values = [[cell.label]];
  label.format.font.bold = true;
  label.format.font.color = LABEL_COLOR;

  const target = ws.getRange(cell.address);
  if (cell.value !== undefined) target.values = [[cell.value]];
  if (cell.format) target.numberFormat = [[cell.format]];
  for (const area of cell.areas ?? []) {
    // numberFormat needs one entry per cell, so size the array to the range.
    const [first, last = first] = area.range.split(":");
    const from = parseAddress(first);
    const to = parseAddress(last);
    ws.getRange(area.range).numberFormat = Array.from({ length: to.row - from.row + 1 }, () =>
      Array.from({ length: to.col - from.col + 1 }, () => area.format)
    );
  }
  if (isAnswer) markAnswer(target);
}

function writeTable(ws: Excel.Worksheet, spec: TableSpec) {
  const headers = allHeaders(spec);
  const { col, row } = parseAddress(spec.anchor);
  const lastCol = columnLetter(col + headers.length - 1);
  const answerBlanks = (spec.answerColumns ?? []).map(() => "");
  // A leading apostrophe stores the value as typed text.
  const textIdx = new Set((spec.textColumns ?? []).map((h) => spec.headers.indexOf(h)));
  const rows = spec.rows.map((values) =>
    values.map((v, i) => (textIdx.has(i) && typeof v === "string" && v !== "" ? `'${v}` : v))
  );

  const table = ws.tables.add(`${spec.anchor}:${lastCol}${row}`, true);
  table.name = spec.name;
  table.getHeaderRowRange().values = [headers];
  table.rows.add(
    undefined,
    rows.map((values) => [...values, ...answerBlanks])
  );
  table.style = "TableStyleMedium2";

  for (const [header, format] of Object.entries(spec.formats ?? {})) {
    ws.getRange(tableColumnRange(spec, header)).numberFormat = spec.rows.map(() => [format]);
  }
  for (const header of spec.answerColumns ?? []) {
    markAnswer(ws.getRange(tableColumnRange(spec, header)));
  }

  ws.getRange(`${spec.anchor}:${lastCol}${row + spec.rows.length}`).format.autofitColumns();
}

function writeGrid(ws: Excel.Worksheet, grid: GridSpec) {
  const { col, row } = parseAddress(grid.anchor);
  const lastCol = columnLetter(col + grid.colHeaders.length);
  const header = ws.getRange(`${grid.anchor}:${lastCol}${row}`);
  header.values = [[grid.corner, ...grid.colHeaders]];
  header.format.font.bold = true;
  header.format.font.color = "#FFFFFF";
  header.format.fill.color = "#2E5C9A";

  const left = columnLetter(col);
  const rowHeaders = ws.getRange(`${left}${row + 1}:${left}${row + grid.rowHeaders.length}`);
  rowHeaders.values = grid.rowHeaders.map((value) => [value]);
  rowHeaders.format.font.bold = true;
  rowHeaders.format.fill.color = "#DDEBF7";

  const body = ws.getRange(gridBodyRange(grid));
  if (grid.format) {
    body.numberFormat = grid.rowHeaders.map(() => grid.colHeaders.map(() => grid.format!));
  }
  markAnswer(body);
  ws.getRange(`${grid.anchor}:${lastCol}${row + grid.rowHeaders.length}`).format.autofitColumns();
}

/** Builds every sheet of a Funciones project from its spec. */
export async function buildFuncionProject(spec: FuncionProjectSpec): Promise<void> {
  await Excel.run(async (context) => {
    for (const sheet of spec.sheets) {
      await safeDeleteSheet(context, sheet.name);
    }

    let first: Excel.Worksheet | null = null;
    for (const sheet of spec.sheets) {
      const ws = context.workbook.worksheets.add(sheet.name);
      first ??= ws;

      for (const table of sheet.tables) writeTable(ws, table);
      for (const grid of sheet.grids ?? []) writeGrid(ws, grid);
      for (const input of sheet.inputs ?? []) writeLabelled(ws, input, false);
      for (const answer of sheet.answers ?? []) writeLabelled(ws, answer, true);

      const labelled = [...(sheet.inputs ?? []), ...(sheet.answers ?? [])];
      for (const cell of labelled.filter((item) => item.labelPosition !== "above")) {
        const { col } = parseAddress(cell.address);
        const first = cell.autofit === false ? col - 1 : col;
        ws.getRange(`${columnLetter(col - 1)}:${columnLetter(first)}`).format.autofitColumns();
      }
      // Autofit has to run before the title: it measures the whole column,
      // and the long subtitle in A2 would stretch column A.
      await context.sync();

      ws.getRange("A1").values = [[sheet.title]];
      ws.getRange("A1").format.font.size = 20;
      ws.getRange("A1").format.font.bold = true;
      ws.getRange("A1").format.font.color = LABEL_COLOR;
      ws.getRange("A2").values = [[sheet.subtitle]];
      ws.getRange("A2").format.font.italic = true;
      ws.getRange("A2").format.font.color = "#666666";
    }

    first?.activate();
    first?.getRange("A1").select();
    await context.sync();
  });
}
